import { Link } from "react-router-dom";
import { Radio, UserX, ShieldCheck, ArrowRight, MapPin, Hash, MousePointerClick, ListChecks, Mail } from "lucide-react";
import { TrackingForm } from "@/components/site/TrackingForm";
import { ServiceGrid } from "@/components/site/ServiceGrid";
import { MapEmbed } from "@/components/site/MapEmbed";
import { COMPANY } from "@/lib/company";

const FEATURES = [
  { icon: Radio, title: "Aggiornamenti automatici", text: "La pagina di tracciamento si aggiorna da sola quando registriamo un nuovo evento." },
  { icon: UserX, title: "Nessuna registrazione", text: "Basta il codice ricevuto: niente account, niente password." },
  { icon: ShieldCheck, title: "Dati protetti", text: "Mostriamo i dati della spedizione, mai numeri di telefono, email o note interne." },
];

const STEPS = [
  { icon: Hash, title: "Ricevi il codice", text: "Al momento dell'affidamento ti inviamo un codice nel formato LGT47563846." },
  { icon: MousePointerClick, title: "Inseriscilo qui", text: "Incolla il codice nel campo di ricerca e premi Traccia spedizione." },
  { icon: ListChecks, title: "Segui ogni fase", text: "Consulta stato, cronologia e consegna prevista in tempo reale." },
];

const Hero = () => (
  <section className="relative grain overflow-hidden min-h-[calc(100vh-5rem)] flex items-center" data-testid="home-hero">
    <img src="/images/hero.jpg" alt="Camion LINCESSO in autostrada" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-transparent to-transparent" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
      <div className="max-w-3xl">
        <p className="rise text-xs sm:text-sm font-semibold uppercase tracking-[0.3em] text-[#E50914]">Trasporti · Spedizioni · Logistica — Taranto</p>
        <h1 className="rise rise-1 mt-5 font-display font-black uppercase tracking-tight leading-[0.92] text-5xl sm:text-6xl lg:text-7xl metal-text" data-testid="hero-title">
          LINCESSO GROUP TRASPORTI
        </h1>
        <p className="rise rise-2 mt-6 text-lg sm:text-xl text-slate-300 font-medium" data-testid="hero-subtitle">La tua merce, la nostra responsabilità.</p>
        <div className="rise rise-3 mt-12">
          <label className="block font-display text-white text-xl sm:text-2xl font-bold uppercase tracking-wide mb-4">Traccia la tua spedizione</label>
          <TrackingForm size="hero" testPrefix="hero" />
          <p className="mt-4 text-xs sm:text-sm text-slate-400">Il codice è indicato nel messaggio ricevuto al momento dell'affidamento della merce.</p>
        </div>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <section className="bg-[#0B0C0E] border-y border-white/10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10">
          {FEATURES.map((f) => (
            <div key={f.title} className="flex gap-4 p-8 lg:p-10">
              <f.icon className="h-6 w-6 text-[#E50914] shrink-0" />
              <div>
                <h3 className="font-display text-white text-lg font-bold uppercase tracking-wide">{f.title}</h3>
                <p className="mt-1 text-sm text-slate-400">{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#0B0C0E] py-24 lg:py-32" data-testid="home-about">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 relative">
            <img src="/images/warehouse.jpg" alt="Magazzino logistico" className="w-full aspect-[4/3] object-cover" />
            <div className="absolute -bottom-6 -right-2 sm:-right-6 bg-[#E50914] text-white p-6 max-w-[240px]">
              <p className="font-display text-2xl font-black uppercase leading-none">Taranto</p>
              <p className="mt-2 text-xs uppercase tracking-widest">Sede operativa</p>
            </div>
          </div>
          <div className="lg:col-span-6">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#E50914]">Chi siamo</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-white">Affidabilità su ogni chilometro</h2>
            <p className="mt-6 text-slate-400 leading-relaxed">
              {COMPANY.name} è un'azienda di trasporti, spedizioni e logistica con sede a Taranto. Ci occupiamo del trasporto delle merci dei nostri clienti con cura, puntualità e trasparenza.
            </p>
            <p className="mt-4 text-slate-400 leading-relaxed">Ogni spedizione riceve un codice univoco che permette di seguirne ogni fase direttamente dal nostro sito, dall'affidamento fino alla consegna.</p>
            <Link to="/chi-siamo" className="mt-8 inline-flex items-center gap-2 text-white font-semibold uppercase tracking-wider text-sm border-b-2 border-[#E50914] pb-1 hover:gap-4 transition-[gap] duration-300" data-testid="home-about-link">
              Scopri di più <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-black py-24 lg:py-32 speed-lines" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#E50914]">I nostri servizi</p>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-white">Trasporto e logistica</h2>
            </div>
            <Link to="/servizi" className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm font-semibold uppercase tracking-wider" data-testid="home-services-link">
              Tutti i servizi <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <ServiceGrid />
        </div>
      </section>

      <section className="bg-[#0B0C0E] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#E50914]">Come funziona</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-white">Tracciare è semplice</h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-10">
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative border-t-2 border-white/10 pt-8">
                <span className="absolute -top-[2px] left-0 h-[2px] w-16 bg-[#E50914]" />
                <span className="font-mono text-5xl font-bold text-white/10">0{i + 1}</span>
                <s.icon className="mt-4 h-7 w-7 text-white" />
                <h3 className="mt-4 font-display text-xl font-bold uppercase text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-black py-24 lg:py-32 border-t border-white/10" data-testid="home-contacts">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#E50914]">Sede e contatti</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-white">Dove trovarci</h2>
            <div className="mt-8 flex gap-4 text-slate-300">
              <MapPin className="h-6 w-6 text-[#E50914] shrink-0" />
              <div>
                <p className="font-semibold text-white">{COMPANY.name}</p>
                <p>{COMPANY.street}</p>
                <p>{COMPANY.cap} {COMPANY.city} ({COMPANY.province}) — {COMPANY.country}</p>
              </div>
            </div>
            <div className="mt-6 flex gap-4 text-slate-300">
              <Mail className="h-6 w-6 text-[#E50914] shrink-0" />
              <div>
                <p className="font-semibold text-white">Assistenza</p>
                <a href={`mailto:${COMPANY.supportEmail}`} className="hover:text-white underline underline-offset-4 break-all" data-testid="home-support-email">{COMPANY.supportEmail}</a>
              </div>
            </div>
            <Link to="/contatti" className="mt-10 inline-flex items-center gap-3 bg-[#E50914] hover:bg-[#C10710] text-white px-7 py-4 font-display font-bold uppercase tracking-wider transition-colors" data-testid="home-contact-btn">
              Scrivici un messaggio <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
          <div className="lg:col-span-7 border border-white/10">
            <MapEmbed className="h-80 lg:h-96" />
          </div>
        </div>
      </section>
    </>
  );
}
